import React, { useState } from "react";
import {
  isValidPhoneNumber,
  getCountryCallingCode,
} from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { useForm } from "react-hook-form";
import styles from "./AlumniFormPopup.module.css";
import axios from "axios";
import { X } from "lucide-react";
import { toast } from "react-hot-toast";
import OTPScreen from "../OTP/OTPScreen";
import { removeCountryCode } from "@/utils/utils";
import { sentOtpAction } from "@/services/PreEnrollmentService";
import UpdatePhone from "../OTP/UpdatePhone";
import InitialForm from "./components/InitialForm/InitialForm";
import AlumniRegistrationStep from "./components/AlumniRegistrationStep/AlumniRegistrationStep";
import DonationForm from "./components/DonationForm/DonationForm";
import {
  checkAlumniUserExists,
  registerAlumniUser,
} from "@/services/CmsService";
import Image from "next/image";
import { Modal } from "react-bootstrap";

interface FormData {
  name: string;
  email: string;
  phone: string;
}

interface AlumniFormData {
  studentNumber: string;
  graduationYear: string;
  dateOfBirth: string;
  regenesysQualification: string;
  currentOccupation: string;
  companyName: string;
}

interface AlumniFormPopupProps {
  show: boolean;
  onHide: () => void;
  AlumniFormEnable?: boolean;
  data?: any;
}

const AlumniFormPopup: React.FC<AlumniFormPopupProps> = ({
  data,
  show,
  onHide,
  AlumniFormEnable,
}) => {
  const [mobileNumber, setMobileNumber] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step, setStep] = useState(1);
  const [isFocused, setIsFocused] = useState(false);
  const [phoneNumberError, setPhoneNumberError] = useState<string>("");
  const [countryCode, setCountryCode] = useState("");
  const [isUserExist, setIsUserExist] = useState(false);
  const [initialFormData, setInitialFormData] = useState<FormData | null>(null);

  const handleClose = () => {
    setMobileNumber("");
    setIsSubmitting(false);
    setStep(1);
    setIsFocused(false);
    setPhoneNumberError("");
    setCountryCode("");
    setIsUserExist(false);
    setInitialFormData(null);

    const form = document.querySelector("form");
    if (form) {
      form.reset();
    }

    onHide();
  };

  const handleCountryCodeChange = (country: any) => {
    if (country) {
      const callingCode = getCountryCallingCode(country);
      setCountryCode(callingCode);
    } else {
      setCountryCode("");
    }
  };

  const mobileWithoutCode = removeCountryCode(countryCode, mobileNumber);

  const checkExistingUser = async (
    phoneNumber: string,
    email: string
  ): Promise<boolean> => {
    try {
      const response = await checkAlumniUserExists(phoneNumber, email);

      if (!response || !response.data) {
        setIsUserExist(false);
        return false;
      }

      const userExists = response.data.length > 0;
      setIsUserExist(userExists);
      return userExists;
    } catch (error) {
      toast.error("Error checking existing user", { position: "top-center" });
      return false;
    }
  };

  const handlesendOTP = async () => {
    const payload = {
      mobileNumber: mobileWithoutCode,
      mobileCountryCode: countryCode,
    };

    try {
      const response = await sentOtpAction(payload);
      if (response.statusCode === 201) {
        toast.success("OTP send successfully", {
          position: "top-center",
        });
      } else {
        toast.error("Failed to send OTP", { position: "top-center" });
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        toast.error(error.response?.data?.message || "Failed to send OTP", {
          position: "top-center",
        });
      } else {
        toast.error("Failed to send OTP", { position: "top-center" });
      }
    }
  };

  const handleChangePhoneNumber = () => {
    setStep(3);
  };

  const handleLeadsData = async (is_otp_verified?: 0 | 1) => {
    if (is_otp_verified === 1) {
      toast.success("Yay! You've sucessfully verified your OTP", {
        position: "top-center",
        duration: 3000,
      });
      setStep(4);
    } else {
      toast.error("Wrong OTP. Please try again.", {
        position: "top-center",
        duration: 3000,
      });
    }
  };

  const handleExistingUser = async () => {
    setStep(4);
  };

  const onSubmit = async (data: FormData) => {
    if (!mobileNumber || !isValidPhoneNumber(mobileNumber)) {
      setPhoneNumberError("Valid phone number required");
      return;
    }

    setIsSubmitting(true);

    try {
      setInitialFormData(data);
      const userExists = await checkExistingUser(mobileNumber, data.email);

      if (userExists) {
        if (AlumniFormEnable) {
          toast.error("Oops! This contact is already registered.");
          setIsSubmitting(false);

          return;
        } else {
          setStep(5);
          toast.success(
            "You are already registered. Redirecting you to the donation form"
          );
          setIsSubmitting(false);
          return;
        }
      }

      // Only proceed with OTP if user doesn't exist
      await handlesendOTP();
      setStep(2);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        toast.error(
          error.response?.data?.message ||
            "An error occurred while submitting the form",
          { position: "top-center" }
        );
      } else {
        toast.error("An unexpected error occurred", { position: "top-center" });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSubmitAlumniForm = async (
    data: AlumniFormData,
    isSkipped?: boolean
  ) => {
    if (!initialFormData) {
      toast.error("Initial form data is missing", { position: "top-center" });
      return;
    }

    setIsSubmitting(true);
    try {
      if (isSkipped) {
        // Handle skip case
        toast.success(
          "Skipped alumni registration form redirecting to the donation",
          {
            position: "top-center",
            duration: 3000,
          }
        );
        setStep(5);
        setIsSubmitting(false);
        return;
      }

      const alumniPayload = {
        full_name: initialFormData.name,
        email: initialFormData.email,
        phone_number: mobileNumber,
        student_number: data.studentNumber,
        most_recent_qualification: data.regenesysQualification,
        current_occupation: data.currentOccupation || "",
        company_name: data.companyName || "",
        graduation_year: data.graduationYear || "",
        date_of_birth: data.dateOfBirth || "",
      };

      const response = await registerAlumniUser(alumniPayload);

      if (response?.data) {
        if (AlumniFormEnable) {
          toast.success("Yay! You've successfully registered as an Alumni", {
            position: "top-center",
            duration: 3000,
          });
          onHide();
          setStep(1);
        } else {
          toast.success(
            "You've successfully registered redirecting to the donation",
            {
              position: "top-center",
              duration: 3000,
            }
          );
          setStep(5);
        }
      } else {
        throw new Error("Registration failed");
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const errorData = error.response?.data?.error;
        const specificError = errorData?.details?.errors?.[0];

        let errorMessage = "Failed to submit alumni form";

        if (
          specificError?.path?.[0] === "student_number" &&
          specificError?.message === "This attribute must be unique"
        ) {
          errorMessage = "This student number is already registered.";
        } else if (errorData?.message) {
          errorMessage = errorData.message;
        }

        toast.error(errorMessage, { position: "top-center" });
      } else {
        toast.error("Failed to submit alumni form", { position: "top-center" });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      size="lg"
      dialogClassName={styles.customModalDialog}
      contentClassName={styles.modalContent}
      backdrop="static"
      onExited={() => {
        setMobileNumber("");
        setStep(1);
        const form = document.querySelector("form");
        if (form) {
          form.reset();
        }
      }}
    >
      <Modal.Body className="p-0">
        <div className={styles.popupContainer}>
          <span className={styles.CloseButton} onClick={handleClose}>
            <X />
          </span>
          <div className={styles.leftSection}>
            <div className={styles.imageWrapper}>
              <Image
                src={data?.images[0]?.url || ""}
                alt={data?.images[0]?.alternativeText || "Graduate"}
                layout="fill"
                objectFit="cover"
                className={styles.graduateImage}
                priority
              />
            </div>
          </div>
          <div className={styles.rightSection}>
            {step === 1 && (
              <div
                className={styles.rightSectionContainer}
                style={{ flexDirection: "row" }}
              >
                <InitialForm
                  onSubmit={onSubmit}
                  isSubmitting={isSubmitting}
                  mobileNumber={mobileNumber}
                  setMobileNumber={setMobileNumber}
                  handleCountryCodeChange={handleCountryCodeChange}
                  phoneNumberError={phoneNumberError}
                  setPhoneNumberError={setPhoneNumberError}
                  isFocused={isFocused}
                  setIsFocused={setIsFocused}
                  checkExistingUser={checkExistingUser}
                  title={
                    data?.title_description?.title ||
                    "Register to start your wonderful journey as alumni!"
                  }
                />
              </div>
            )}
            {step === 2 && (
              <div className={styles.rightSectionContainer}>
                <OTPScreen
                  handleChangePhoneNumber={handleChangePhoneNumber}
                  mobileNumber={mobileNumber}
                  mobileWithoutCode={mobileWithoutCode}
                  countryCode={countryCode}
                  resendOTP={handlesendOTP}
                  handleLeadsData={handleLeadsData}
                  setStep={setStep}
                  handleExistingUser={handleExistingUser}
                />
              </div>
            )}
            {step === 3 && (
              <div className={styles.rightSectionContainer}>
                <UpdatePhone
                  setStep={setStep}
                  mobileNumber={mobileNumber}
                  setMobileNumber={setMobileNumber}
                  handleCountryCodeChange={handleCountryCodeChange}
                  handlesendOTP={handlesendOTP}
                />
              </div>
            )}
            {step === 4 && (
              <AlumniRegistrationStep
                onSubmit={onSubmitAlumniForm}
                isSubmitting={isSubmitting}
                AlumniFormEnable={AlumniFormEnable}
              />
            )}
            {step === 5 && !AlumniFormEnable && (
              <div className={styles.rightSectionContainer}>
                <DonationForm
                  userEmail={initialFormData?.email}
                  userName={initialFormData?.name}
                />
              </div>
            )}
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default AlumniFormPopup;
